import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'd2p_cart';

function loadCartFromStorage() {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Failed to load cart from storage:', e);
  }
  return [];
}

function saveCartToStorage(items) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.warn('Failed to save cart to storage:', e);
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCartFromStorage);

  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems(prev => {
      const key = product.sku || product.id;
      const existing = prev.find(i => (i.sku || i.id) === key);
      if (existing) {
        return prev.map(i =>
          (i.sku || i.id) === key
            ? { ...i, qty: i.qty + qty }
            : i
        );
      }
      return [...prev, {
        id: product.id,
        sku: product.sku || product.id,
        name: product.title || product.name,
        price: product.price?.price ?? product.price ?? 0,
        qty,
        thumbnailUrl: product.thumbnailUrl || null,
        categories: product.categories || [],
        _isStatic: product._isStatic || false,
        _staticEmoji: product._staticEmoji || null
      }];
    });
  }, []);

  const removeItem = useCallback((itemKey) => {
    setItems(prev => prev.filter(i => (i.sku || i.id) !== itemKey));
  }, []);

  const updateQty = useCallback((itemKey, qty) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => (i.sku || i.id) !== itemKey));
      return;
    }
    setItems(prev =>
      prev.map(i =>
        (i.sku || i.id) === itemKey ? { ...i, qty } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = items.reduce((sum, i) => sum + (i.price * i.qty), 0);

  const value = {
    items,
    itemCount,
    subtotal,
    addItem,
    removeItem,
    updateQty,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
